import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    
    // Ensure free products are at quantity 1 on load
    this.#checkFreeProductsQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    // Prevent quantity changes for free products (except removal - quantity 0)
    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (lineItemRow?.dataset.freeProduct === 'true' && quantity > 0) {
      // Reset quantity to 1 for free products
      const quantitySelector = this.refs.quantitySelectors[line - 1];
      const quantityInput = quantitySelector?.querySelector('input');
      if (quantityInput) {
        quantityInput.value = '1';
      }
      return;
    }

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  async onLineItemRemove(line) {
    // Get the original item before removing free products (to find it later if line numbers change)
    let originalItem = null;
    try {
      const cartResponse = await fetch(Theme.routes.root + 'cart.js');
      const cartData = await cartResponse.json();
      originalItem = cartData.items[line - 1];
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
    
    // Check if this item has associated free products before removing
    // We need to do this BEFORE removing the item, so line numbers are correct
    const freeProductsRemoved = await this.#removeAssociatedFreeProducts(line);
    
    // If free products were removed, wait a bit for UI to update and re-fetch cart
    // to get updated line numbers for the main product
    if (freeProductsRemoved && originalItem) {
      // Small delay to ensure cart updates are processed
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Re-fetch cart to get updated line numbers
      try {
        const updatedCartResponse = await fetch(Theme.routes.root + 'cart.js');
        const updatedCartData = await updatedCartResponse.json();
        
        // Find the new line number for the item we want to remove by matching variant ID
        const itemToRemove = updatedCartData.items.find((item) => {
          return item.variant_id.toString() === originalItem.variant_id.toString();
        });
        
        if (itemToRemove) {
          const newLine = updatedCartData.items.indexOf(itemToRemove) + 1;
          line = newLine;
        }
      } catch (error) {
        console.error('Error updating line number after free product removal:', error);
      }
    }

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const remove = () => cartItemRowToRemove.remove();

    if (prefersReducedMotion()) return remove();

    // Add class to the row to trigger the animation
    cartItemRowToRemove.style.setProperty('--row-height', `${cartItemRowToRemove.clientHeight}px`);
    cartItemRowToRemove.classList.add('removing');

    // Remove the row after the animation ends
    onAnimationEnd(cartItemRowToRemove, remove);
  }

  /**
   * Removes free products associated with a zero-quantity product.
   * @param {number} line - The line item index of the product being removed.
   * @returns {Promise<boolean>} - Returns true if free products were removed, false otherwise.
   */
async #removeAssociatedFreeProducts(line) {
  try {
    const cartResponse = await fetch(Theme.routes.root + 'cart.js');
    const cartData = await cartResponse.json();

    const parentItem = cartData.items[line - 1];
    if (!parentItem) return false;

    const parentVariantId = parentItem.variant_id.toString();

    // Check if there are other non-free products in cart that qualify for free gifts
    // (rebound collection products or other backorder products)
    // If yes, don't remove free products as they might be from other qualifying products
    // Only keep free products if there is another logic product in the cart that qualifies for the same gift.
    // Check for rebound/x-bound/x-tense collection or a backorder-eligible item.
    const eligibleCollectionHandles = ['rebound-boots', 'x-bound', 'x-tense'];
    let hasQualifyingProduct = false;
    for (const item of cartData.items) {
      if (!item.properties) continue;
      // Skip the item being removed
      if (item.variant_id.toString() === parentVariantId) continue;
      // Skip free products
      const isFree =
        item.properties['_Free Product'] === 'Yes' ||
        item.properties._Free_Product === 'Yes' ||
        item.properties['_Free Product'] != null;
      if (isFree) continue;
      // Flag set on qualifying products when they are added to the cart (covers rebound/backorder)
      const hasGiftEligibleFlag =
        item.properties['_Gift Eligible'] === 'true' ||
        item.properties._Gift_Eligible === 'true' ||
        item.properties['_Gift Eligible'] != null ||
        item.properties._Gift_Eligible != null;
      if (hasGiftEligibleFlag) {
        hasQualifyingProduct = true;
        break;
      }

      // Backorder: check for backorder property (legacy eligibility)
      const hasBackOrderProp = Object.keys(item.properties).some((key) => key.toLowerCase().includes('back') && key.toLowerCase().includes('order'));
      const isEligibleBackorder = hasBackOrderProp && item.properties['_Eligible Collection Backorder'] === 'true';
      if (isEligibleBackorder) {
        hasQualifyingProduct = true;
        break;
      }
      // Rebound type: check collection or tags
      if (
        (item.product && item.product.collections && item.product.collections.some((col) => eligibleCollectionHandles.includes(col.handle))) ||
        (item.product && item.product.tags && item.product.tags.some((tag) => eligibleCollectionHandles.includes(tag)))
      ) {
        hasQualifyingProduct = true;
        break;
      }
    }
    if (hasQualifyingProduct) {
      return false;
    }

    // No qualifying products remain; remove all free products still in cart
    const freeProductsToRemove = [];
    cartData.items.forEach((item) => {
      if (!item.properties) return;

      const isFree =
        item.properties['_Free Product'] === 'Yes' ||
        item.properties._Free_Product === 'Yes' ||
        item.properties['_Free Product'] != null;

      if (isFree) {
        freeProductsToRemove.push({
          variantId: item.variant_id,
        });
      }
    });

    if (!freeProductsToRemove.length) return false;

    // Collect sections to update
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    let removedCount = 0;

    // Remove each free product safely by re-fetching cart to get correct current line
    for (const fp of freeProductsToRemove) {
      const currentCartRes = await fetch(Theme.routes.root + 'cart.js');
      const currentCart = await currentCartRes.json();

      let currentLine = null;
      currentCart.items.forEach((it, idx) => {
        if (!it.properties) return;

        const isFree =
          it.properties['_Free Product'] === 'Yes' ||
          it.properties._Free_Product === 'Yes' ||
          it.properties['_Free Product'] != null;

        if (
          isFree &&
          it.variant_id.toString() === fp.variantId.toString()
        ) {
          currentLine = idx + 1;
        }
      });

      if (!currentLine) continue;

      const body = JSON.stringify({
        line: currentLine,
        quantity: 0,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });

      const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
      const parsed = await response.json();

      if (!parsed.errors && parsed.sections) {
        Object.keys(parsed.sections).forEach((sectionId) => {
          morphSection(sectionId, parsed.sections[sectionId]);
        });
        removedCount++;
      }
    }

    return removedCount > 0;
  } catch (error) {
    console.error('Error removing associated free products:', error);
    return false;
  }
}


  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    
    // Prevent quantity changes for free products (except removal - quantity 0)
    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (lineItemRow?.dataset.freeProduct === 'true' && quantity > 0) {
      this.#enableCartItems();
      const quantitySelector = this.refs.quantitySelectors[line - 1];
      const quantityInput = quantitySelector?.querySelector('input');
      if (quantityInput) {
        quantityInput.value = '1';
      }
      return;
    }
    
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element (includes all items including free products)
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Checks and ensures free products are at quantity 1 on component load.
   */
  #checkFreeProductsQuantity = () => {
    // Check after a short delay to ensure DOM is ready
    setTimeout(() => {
      const freeProductRows = this.querySelectorAll('[data-free-product="true"]');
      freeProductRows.forEach(row => {
        const quantitySelector = row.querySelector('quantity-selector');
        if (quantitySelector) {
          const input = quantitySelector.querySelector('input');
          if (input && input.value !== '1') {
            input.value = '1';
          }
        }
      });
    }, 100);
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
